import { useEffect, useState } from "react"
import classNames from "classnames"
import NextImage from "next/image"
import { useStore } from "@features/common"
import { Slider, SliderSlide } from "@ui"
import { observer } from "mobx-react"
import { IconTikTok } from "@lib/icons"
import { SOCIAL_POST_CLIPPATH_ID, TikTokBorder } from "@features/journal"
import { TikTokModel } from "@serializers/tiktok"

const DEFAULT_TIKTOK_URL = "https://www.tiktok.com/@thepureapp"
const DEFAULT_TIKTOK_NAME = "TIK TOK"

export const TikTokCarousel = observer(function TikTokCarousel() {
  const {
    BlogModel: { tiktok },
    GlobalModel,
    ResponsiveModel,
  } = useStore()
  const [isWindows, setIsWindows] = useState<boolean>(false)

  useEffect(() => {
    setIsWindows(ResponsiveModel.isWindowsOS)
  }, [ResponsiveModel.isWindowsOS])

  if (!tiktok.length) {
    return null
  }

  const tiktokHref =
    GlobalModel.getTemplate("tiktok_link") || DEFAULT_TIKTOK_URL
  const tiktokName =
    GlobalModel.getTemplate("tiktok_name") || DEFAULT_TIKTOK_NAME

  const slidesCount = Math.min(Math.floor(ResponsiveModel.width / 274), 5)

  return (
    <section className="bg-gray-50 relative" data-test-id="tiktok_section">
      <TikTokBorder className="absolute text-gray-50 h-3.5 w-full -top-3.5" />

      <div className="container pt-11 px-0 pb-20 tablet:pt-20 tablet:pb-30 laptop:px-22 desktop:px-23 desktop-l:px-22">
        <div className="flex px-4 mb-13 tablet:px-6 tablet:mb-12 laptop:justify-center">
          <a
            href={tiktokHref}
            target="_blank"
            rel="noreferrer"
            className={classNames(
              "flex space-x-4 tablet:space-x-5 transition-opacity duration-150 ease-in hover:opacity-60",
              isWindows && "items-center",
            )}
            data-test-id="tiktok_section__title"
          >
            <span className="py-1 text-2xl tablet:text-4xl uppercase tracking-2">
              {tiktokName}
            </span>
            <IconTikTok />
          </a>
        </div>
        {tiktok.length > slidesCount ? (
          <Slider
            options={{
              spaceBetween: -16,
              slidesPerView: "auto",
              navigation: true,
              breakpoints: {
                600: {
                  spaceBetween: -36,
                },
                1024: {
                  slidesPerView: 3,
                  spaceBetween: 48,
                },
                1280: {
                  slidesPerView: 4,
                  spaceBetween: 48,
                },
                1440: {
                  slidesPerView: 5,
                  spaceBetween: 23,
                },
              },
            }}
          >
            {tiktok.map((post) => (
              <SliderSlide
                key={post.url}
                className="w-auto flex justify-center px-4 tablet-s:px-6 laptop:px-0"
              >
                <TikTokPreview key={post.url} post={post} />
              </SliderSlide>
            ))}
          </Slider>
        ) : (
          <div className="flex justify-center space-x-10">
            {tiktok.map((post) => (
              <TikTokPreview key={post.url} post={post} />
            ))}
          </div>
        )}
      </div>
    </section>
  )
})

function TikTokPreview(props: { post: TikTokModel }) {
  const { post } = props
  return (
    <a
      className="group block w-[234px] h-104 relative"
      href={post.url}
      style={{
        clipPath: `url(#${SOCIAL_POST_CLIPPATH_ID})`,
      }}
      data-test-id="tiktok_section__post"
      target="_blank"
      rel="noreferrer"
      draggable={false}
    >
      <NextImage
        src={post.image}
        layout="fill"
        objectFit="cover"
        unoptimized={true}
      />
      <div className="absolute w-full h-full flex justify-center items-center text-white opacity-0 bg-black bg-opacity-40 cursor-pointer transition-opacity group-hover:opacity-100">
        <IconTikTok className="w-16 h-16" />
      </div>
    </a>
  )
}
